import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';

import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  CardHeader,
  Typography,
  Autocomplete,
  TableContainer,
  CircularProgress,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { IProviderUser } from 'src/services/integration/integrations.types';
import { useGetAccountsQuery } from 'src/services/organisation/organisation.service';
import {
  useGetCommunicationUserMappingsQuery,
  useGetCommunicationProviderUsersQuery,
  useUpsertCommunicationUserMappingMutation,
} from 'src/services/integration/integrations.service';

// ----------------------------------------------------------------------

type AccountOption = { id: string; label: string };

/**
 * Maps each provider (Dialpad) user to a JobTetris user. The mapping makes
 * inbound calls/SMS attribute to the right recruiter on the timeline, and is
 * required for that user to place calls / send SMS as themselves.
 */
export default function UserMappingView() {
  const { t } = useTranslate();
  const authContext = useAuthContext();
  const orgId = authContext?.organization?.id;

  const { data: providerUsers, isLoading: loadingProviders } = useGetCommunicationProviderUsersQuery();
  const { data: mappings } = useGetCommunicationUserMappingsQuery();
  const { data: accountsPage } = useGetAccountsQuery(
    { org_id: orgId as string, page: { page: 1, per_page: 200 } } as any,
    { skip: !orgId }
  );
  const [upsertMapping] = useUpsertCommunicationUserMappingMutation();

  const accountOptions: AccountOption[] = useMemo(() => {
    const items = (accountsPage?.results ?? []) as any[];
    return items.map((a) => {
      const u = a.user ?? a;
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      return { id: a.id, label: name || u.email || a.id };
    });
  }, [accountsPage]);

  const mappingByProvider = useMemo(() => {
    const map: Record<string, string | null> = {};
    (mappings ?? []).forEach((m) => { map[m.provider_user_id] = m.jobtetris_user_id; });
    return map;
  }, [mappings]);

  const handleChange = async (pu: IProviderUser, option: AccountOption | null) => {
    try {
      await upsertMapping({
        provider_user_id: pu.id,
        jobtetris_user_id: option?.id ?? null,
        provider_user_email: pu.email ?? undefined,
        provider_phone_number: pu.phone ?? undefined,
      }).unwrap();
      enqueueSnackbar(t('integrations.communications.mapping.saved'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('integrations.communications.mapping.error'), { variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('integrations.communications.mapping.title')}
        subheader={t('integrations.communications.mapping.subtitle')}
      />

      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('integrations.communications.mapping.provider_user')}</TableCell>
              <TableCell>{t('integrations.communications.mapping.jobtetris_user')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loadingProviders && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!loadingProviders && (providerUsers ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('integrations.communications.mapping.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loadingProviders && (providerUsers ?? []).map((pu) => {
              const currentId = mappingByProvider[pu.id] ?? null;
              const current = accountOptions.find((o) => o.id === currentId) ?? null;
              return (
                <TableRow key={pu.id} hover>
                  <TableCell>
                    <Typography variant="body2">{pu.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[pu.email, pu.phone].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 340 }}>
                    <Autocomplete
                      size="small"
                      options={accountOptions}
                      value={current}
                      onChange={(_e, option) => handleChange(pu, option)}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      getOptionLabel={(o) => o.label}
                      renderInput={(params) => (
                        <TextField {...params} placeholder={t('integrations.communications.mapping.unmapped')} />
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
